import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { logActivity } from "../../utils/activity";
import { gmPeopleFromConfig, ownPersonFromConfig } from "../../utils/users";
import { loadGmHudConfig } from "../../utils/config";
import { todayStr } from "../../utils/dateRange";
import { formatDate, formatLongDate, formatTime, formatWeekdayDate } from "../../utils/format";
import "./Checklist.css";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const LIST_LABELS = { open: "Åbneliste", day: "Todo i dag", close: "Lukkeliste" };

export default function Checklist() {
    const navigate = useNavigate();
    const userEmail = localStorage.getItem("userEmail") || "";
    const date = todayStr();

    const [config, setConfig] = useState(null);
    const [itemsByLocation, setItemsByLocation] = useState({}); // locKey -> items[]
    const [loading, setLoading] = useState(true);
    // Set when the checklist feature is not rolled out to this user. Mirrors the "checklist"
    // row in the features sheet; the same rollout is enforced on /checklist/day and
    // /checklist/complete, so this only saves the user a page that would fail anyway.
    // The task awaiting a "who did it?" choice, or null. Set when a checkbox is ticked.
    const [pending, setPending] = useState(null); // { location, taskId, title }
    const [query, setQuery] = useState(""); // name search in the picker popup
    // Sections the user has clicked. For a category that simply means "collapsed"; for a whole
    // list it flips the list away from its default (see listCollapsed).
    const [toggled, setToggled] = useState(() => new Set());

    const toggleSection = key => setToggled(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    const allowedLocations = useMemo(
        () => config?.users?.[userEmail]?.locations || [],
        [config, userEmail]
    );
    const ownPerson = useMemo(() => ownPersonFromConfig(config), [config]);
    const ownName = ownPerson.name;
    // People picklist for the popup ({name, employeeId}): the logged-in GM first, then the rest.
    const pickPeople = useMemo(() => {
        const all = gmPeopleFromConfig(config);
        if (!ownName) return all;
        return [
            ...all.filter(p => p.name === ownName),
            ...all.filter(p => p.name !== ownName),
        ];
    }, [config, ownName]);

    // Load config once → allowed locations, name picklist. The todo lists are not gated on a
    // feature rollout: everyone on shift needs to be able to tick things off.
    useEffect(() => {
        loadGmHudConfig().then(setConfig);
    }, [userEmail]);

    const fetchDay = useCallback(async (locations) => {
        const token = localStorage.getItem("accessToken");
        if (!token || !GAMES_CLOUD_BASE) { navigate("/"); return; }
        setLoading(true);
        try {
            const results = await Promise.all(locations.map(async loc => {
                const r = await fetch(
                    `${GAMES_CLOUD_BASE}checklist/day?date=${date}&location=${encodeURIComponent(loc)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!r.ok) return [loc, []];
                const data = await r.json();
                return [loc, data.items || []];
            }));
            setItemsByLocation(Object.fromEntries(results));
        } catch {
            setItemsByLocation({});
        } finally {
            setLoading(false);
        }
    }, [date, navigate]);

    useEffect(() => {
        if (allowedLocations.length) fetchDay(allowedLocations);
        else if (config) setLoading(false);
    }, [allowedLocations, config, fetchDay]);

    // Complete a task with the chosen person (name + employee ID, picked in the popup).
    async function completeTask(location, taskId, person, title) {
        const token = localStorage.getItem("accessToken");
        if (!token) { navigate("/"); return; }
        // Optimistic: mark done immediately, roll back on failure.
        const prev = itemsByLocation;
        setItemsByLocation(s => ({
            ...s,
            [location]: s[location].map(it =>
                it.task_id === taskId
                    ? { ...it, done_by_name: person.name, completed_at: Date.now() }
                    : it),
        }));
        try {
            const r = await fetch(`${GAMES_CLOUD_BASE}checklist/complete`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ task_id: taskId, date, name: person.name, employee_id: person.employeeId }),
            });
            if (!r.ok) throw new Error("complete failed");
            logActivity("checklist_task_done", `${title} — ${person.name}`);
        } catch {
            setItemsByLocation(prev); // roll back
            alert("Kunne ikke gemme — prøv igen.");
        }
    }

    function pickPerson(person) {
        const p = pending;
        setPending(null);
        if (p) completeTask(p.location, p.taskId, person, p.title);
    }

    function handleLogout() {
        logActivity("logout");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userEmail");
        navigate("/");
    }

    const locationName = key =>
        config?.locations?.[key]?.displayName || key;

    function renderItem(location, it) {
        const done = !!it.completed_at;
        // A window task: may be done any day up to its deadline. Outstanding and still inside
        // the window, it shows the deadline; past it, it is overdue like any other task.
        const hasWindow = !!it.deadline && it.deadline !== it.due_date;
        const dueSoon = hasWindow && !done && !it.carried && it.deadline === date;
        return (
            <li key={it.task_id} className={`cl-item ${done ? "done" : ""} ${it.carried && !done ? "carried" : ""} ${dueSoon ? "due-soon" : ""}`}>
                <label className="cl-check">
                    <input
                        type="checkbox"
                        checked={done}
                        disabled={done}
                        onChange={() => { setQuery(""); setPending({ location, taskId: it.task_id, title: it.title }); }}
                    />
                    <span className="cl-title">{it.title}</span>
                </label>
                {it.carried && !done && (
                    <span className="cl-overdue" title={`Skulle have været gjort senest ${formatWeekdayDate(it.deadline || it.due_date)}`}>
                        Overskredet · {formatDate(it.deadline || it.due_date)}
                    </span>
                )}
                {hasWindow && !done && !it.carried && (
                    <span className={`cl-deadline ${dueSoon ? "today" : ""}`}
                        title={`Må gøres når som helst fra ${formatWeekdayDate(it.due_date)} — skal være gjort senest ${formatWeekdayDate(it.deadline)}`}>
                        {dueSoon ? "Senest i dag" : `Senest ${formatWeekdayDate(it.deadline)}`}
                    </span>
                )}
                <span className="cl-points">{it.points} p</span>
                {done && (
                    <span className="cl-by">✓ {it.done_by_name} · {formatTime(it.completed_at)}</span>
                )}
            </li>
        );
    }

    function renderList(location, listType) {
        const items = (itemsByLocation[location] || []).filter(it => it.list_type === listType);
        if (!items.length) {
            return <div className="cl-empty">Ingen opgaver i dag.</div>;
        }
        // Group by category, preserving first-appearance order (items are sorted by sort_order).
        const groups = [];
        const byCat = new Map();
        for (const it of items) {
            const cat = it.category || "";
            if (!byCat.has(cat)) { byCat.set(cat, []); groups.push(cat); }
            byCat.get(cat).push(it);
        }
        // No real categories in use → flat list (unchanged look).
        if (!groups.some(c => c !== "")) {
            return <ul className="cl-list">{items.map(it => renderItem(location, it))}</ul>;
        }
        // Uncategorised tasks collect under an automatic "Andet" section, shown last.
        const ordered = [...groups.filter(c => c !== ""), ...(byCat.has("") ? [""] : [])];
        return (
            <div className="cl-groups">
                {ordered.map(cat => {
                    const catItems = byCat.get(cat);
                    const label = cat === "" ? "Andet" : cat;
                    const key = `${location}/${listType}/${cat}`;
                    const isCollapsed = toggled.has(key);
                    const doneCount = catItems.filter(it => it.completed_at).length;
                    return (
                        <div key={key} className="cl-group">
                            <button className="cl-group-head" onClick={() => toggleSection(key)}>
                                <span className={`cl-caret ${isCollapsed ? "collapsed" : ""}`}>▾</span>
                                <span className="cl-group-name">{label}</span>
                                <span className="cl-group-count">{doneCount}/{catItems.length}</span>
                            </button>
                            {!isCollapsed && (
                                <ul className="cl-list">{catItems.map(it => renderItem(location, it))}</ul>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // A finished list is just noise, so a list where every task is done shows up collapsed;
    // one with anything outstanding shows up open. A click flips it from that default.
    function listCollapsed(key, doneCount, total) {
        const allDone = total > 0 && doneCount === total;
        return toggled.has(key) ? !allDone : allDone;
    }

    // A whole list (open/day/close) as a collapsible block. Collapsed, the heading still shows
    // how far that list has got, so nothing is hidden without a trace.
    function renderListBlock(location, listType, sub = false) {
        const key = `${location}/${listType}`;
        const items = (itemsByLocation[location] || []).filter(it => it.list_type === listType);
        const doneCount = items.filter(it => it.completed_at).length;
        const isCollapsed = listCollapsed(key, doneCount, items.length);
        const Heading = sub ? "h4" : "h3";
        return (
            <>
                <Heading className="cl-list-head">
                    <button className="cl-list-toggle" onClick={() => toggleSection(key)}>
                        <span className={`cl-caret ${isCollapsed ? "collapsed" : ""}`}>▾</span>
                        <span className="cl-list-name">{LIST_LABELS[listType]}</span>
                        <span className="cl-group-count">{doneCount}/{items.length}</span>
                    </button>
                </Heading>
                {!isCollapsed && renderList(location, listType)}
            </>
        );
    }

    return (
        <>
            <Topbar
                userEmail={userEmail}
                onLogout={handleLogout}
            />

            <div className="cl-content">
                <div className="cl-toolbar">
                    <div className="cl-date">{formatLongDate(date)}</div>
                </div>

                {loading ? (
                    <div className="cl-empty">Henter…</div>
                ) : allowedLocations.length === 0 ? (
                    <div className="cl-empty">Ingen lokationer tildelt din bruger.</div>
                ) : (
                    allowedLocations.map(loc => (
                        <div key={loc} className="cl-location">
                            <h2 className="cl-location-name">{locationName(loc)}</h2>
                            <div className="cl-lists">
                                {/* Left column: the day's two fixed routines, opening on top and
                                    closing — the last thing anyone does — as its own section below.
                                    Right column: what has to be done at some point during the day. */}
                                <div className="cl-listbox">
                                    {renderListBlock(loc, "open")}
                                    <div className="cl-sublist">
                                        {renderListBlock(loc, "close", true)}
                                    </div>
                                </div>
                                <div className="cl-listbox">
                                    {renderListBlock(loc, "day")}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {pending && (() => {
                const q = query.trim().toLowerCase();
                const filtered = q ? pickPeople.filter(p => p.name.toLowerCase().includes(q)) : pickPeople;
                return (
                    <div className="cl-modal-overlay" onClick={() => setPending(null)}>
                        <div className="cl-modal" onClick={e => e.stopPropagation()}>
                            <h3>Hvem udførte opgaven?</h3>
                            <p className="cl-modal-task">{pending.title}</p>
                            <input
                                className="cl-search"
                                type="text"
                                autoFocus
                                placeholder="Søg navn…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter" && filtered.length) pickPerson(filtered[0]); }}
                            />
                            <div className="cl-modal-names">
                                {filtered.length === 0 && <div className="cl-empty">Ingen match.</div>}
                                {filtered.map(p => (
                                    <button key={p.name}
                                        className={`cl-name-btn ${p.name === ownName ? "own" : ""}`}
                                        onClick={() => pickPerson(p)}>
                                        {p.name}{p.name === ownName ? " (dig)" : ""}
                                    </button>
                                ))}
                            </div>
                            <button className="cl-modal-cancel" onClick={() => setPending(null)}>Annullér</button>
                        </div>
                    </div>
                );
            })()}
        </>
    );
}
